import apiClient from './apiClient';
import {
  Organization,
  OrganizationBranding,
  OrganizationBrandingUpdate,
  OrganizationUiSettings,
  OrganizationUiSettingsUpdate,
  OrganizationCreate,
  OrganizationUpdate,
  OrganizationWithUsers,
  User,
} from '../types';

const apiBaseURL = '/organizations/';

export type GetOrganizationsParams = {
  /** When true, include deactivated organizations (default: true for super-admin list page) */
  includeInactive?: boolean;
};

const organizationService = {
  /**
   * Get all organizations (super-admin only).
   * Pass { includeInactive: true } to include deactivated orgs (e.g. super-admin organizations page).
   */
  getOrganizations: async (params?: GetOrganizationsParams): Promise<Organization[]> => {
    const includeInactive = params?.includeInactive === true;
    const response = await apiClient.get<Organization[]>(apiBaseURL, {
      params: { include_inactive: includeInactive },
    });
    return response.data;
  },

  /**
   * Get all demo organizations (is_demo=true), super-admin only.
   */
  getDemoOrganizations: async (): Promise<Organization[]> => {
    const response = await apiClient.get<Organization[]>(apiBaseURL, {
      params: { is_demo: true, include_inactive: true },
    });
    return response.data;
  },

  /**
   * Get organization by ID with users (super-admin only)
   */
  getOrganizationById: async (id: string): Promise<OrganizationWithUsers> => {
    const response = await apiClient.get<OrganizationWithUsers>(`${apiBaseURL}${id}`);
    return response.data;
  },

  /**
   * Create a new organization (super-admin only)
   */
  createOrganization: async (organizationData: OrganizationCreate): Promise<Organization> => {
    const response = await apiClient.post<Organization>(apiBaseURL, organizationData);
    return response.data;
  },

  /**
   * Update an organization (super-admin only)
   */
  updateOrganization: async (id: string, organizationData: OrganizationUpdate): Promise<Organization> => {
    const response = await apiClient.put<Organization>(`${apiBaseURL}${id}`, organizationData);
    return response.data;
  },

  /**
   * Delete an organization (super-admin only).
   */
  deleteOrganization: async (id: string): Promise<Organization> => {
    const response = await apiClient.delete<Organization>(`${apiBaseURL}${id}`);
    return response.data;
  },

  /**
   * Get users in an organization (super-admin only)
   */
  getOrganizationUsers: async (id: string): Promise<User[]> => {
    const response = await apiClient.get<User[]>(`${apiBaseURL}${id}/users`);
    return response.data;
  },

  /**
   * Create organization with admin user (for initial setup)
   */
  createOrganizationWithAdmin: async (
    organizationData: OrganizationCreate,
    adminData: { email: string; password: string; name?: string }
  ): Promise<Organization> => {
    const response = await apiClient.post<Organization>('/auth/create-organization', {
      organization_data: organizationData,
      admin_data: adminData
    });
    return response.data;
  },

  /**
   * Get the organization of the current user
   */
  getMyOrganization: async (): Promise<Organization> => {
    const response = await apiClient.get<Organization>('/users/me/organization');
    return response.data;
  },

  /**
   * Get an organization's white-label branding (super-admin or org member).
   */
  getBranding: async (id: string): Promise<OrganizationBranding> => {
    const response = await apiClient.get<OrganizationBranding>(`${apiBaseURL}${id}/branding`);
    return response.data;
  },

  /** Effective experience & module settings (any member of the org). */
  getUiSettings: async (id: string): Promise<OrganizationUiSettings> => {
    const response = await apiClient.get<OrganizationUiSettings>(`${apiBaseURL}${id}/ui-settings`);
    return response.data;
  },

  /** Update experience & modules (org admin; analysis_limits is super-admin only). */
  updateUiSettings: async (
    id: string,
    data: OrganizationUiSettingsUpdate
  ): Promise<OrganizationUiSettings> => {
    const response = await apiClient.put<OrganizationUiSettings>(`${apiBaseURL}${id}/ui-settings`, data);
    return response.data;
  },

  /**
   * Apply the "Olisé" preset (org admin or super admin): experience olise,
   * projects/gallery/portfolio modules, private knowledge by default, graphite
   * theme, /projets landing, and app name "Olisé" unless one is already set.
   */
  applyOlisePreset: async (id: string): Promise<OrganizationUiSettings> => {
    const response = await apiClient.post<OrganizationUiSettings>(`${apiBaseURL}${id}/ui-settings/presets/olise`);
    return response.data;
  },

  /**
   * Update an organization's branding color/theme/font (super-admin or org admin).
   */
  updateBranding: async (
    id: string,
    data: OrganizationBrandingUpdate
  ): Promise<OrganizationBranding> => {
    const response = await apiClient.put<OrganizationBranding>(`${apiBaseURL}${id}/branding`, data);
    return response.data;
  },

  /**
   * Upload an organization logo (super-admin or org admin).
   */
  uploadLogo: async (id: string, file: File): Promise<OrganizationBranding> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post<OrganizationBranding>(
      `${apiBaseURL}${id}/branding/logo`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  },

  /**
   * Remove an organization's custom logo (super-admin or org admin).
   */
  deleteLogo: async (id: string): Promise<OrganizationBranding> => {
    const response = await apiClient.delete<OrganizationBranding>(`${apiBaseURL}${id}/branding/logo`);
    return response.data;
  },

  /**
   * Trigger on-demand cleanup of expired demo sessions (super-admin only).
   * Returns the number of sessions deleted.
   */
  triggerDemoCleanup: async (): Promise<{ deleted: number }> => {
    const response = await apiClient.post<{ deleted: number }>('/platform/admin/demo/cleanup');
    return response.data;
  },
};

export default organizationService;
